// Part 2 — product showcase, updated per §4.3/§4.4: the regime panel and the
// static backtest panel are replaced by the interactive PublicDemoWidget
// (real HMM output + real backtest over a frozen 2024–2025 window).
// Caption discipline (§2.5.2) is non-negotiable: every panel carries
// "Sample data, illustrative only — not a recommendation…" on the visual.
import { PublicDemoWidget } from "@/components/PublicDemoWidget";
import { PublicDemoScreener } from "@/components/PublicDemoScreener";
import { DashboardShowcase } from "@/components/DashboardShowcase";

const CAPTION = "Sample data, illustrative only — not a recommendation to buy, sell, or hold any security.";

const SCAN_ROWS = [
  ["NVDA", "Bull Put Spread", 92, "Bull"], ["MSFT", "Covered Call", 87, "Quiet"],
  ["XLE", "Cash-Secured Put", 84, "Bull"], ["KO", "Covered Call", 78, "Quiet"],
];

const Panel = ({ title, children, C, testid }) => (
  <div className="rounded-lg overflow-hidden flex flex-col" style={{ background: C.card, border: `1px solid ${C.border}` }} data-testid={testid}>
    <div className="px-4 pt-3 pb-2 text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: C.muted, fontFamily: "'JetBrains Mono', monospace" }}>{title}</div>
    <div className="px-4 pb-3 flex-1">{children}</div>
    <div className="px-4 py-2 text-[9px] border-t" style={{ color: "#555", borderColor: C.border }}>{CAPTION}</div>
  </div>
);

export const ProductShowcase = ({ C }) => (
  <div>
    {/* §4.4 — interactive public demo: regime chart + backtest, frozen window */}
    <PublicDemoWidget C={C} />

    {/* Premium-selling screener demo — frozen 2025-12-31 snapshot, below the Backtest Lab */}
    <div id="premium-screener" className="mt-4 mb-4" style={{ scrollMarginTop: "60px" }} data-testid="showcase-demo-screener">
      <PublicDemoScreener />
    </div>

    {/* Dashboard preview — frozen capture from a seeded sample account */}
    <div id="dashboard-preview" className="mb-4" style={{ scrollMarginTop: "60px" }} data-testid="showcase-dashboard-wrap">
      <DashboardShowcase C={C} />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="product-showcase-grid">
      <Panel title="Scanner — how the model scores opportunities" C={C} testid="showcase-scanner">
        <table className="w-full text-[10px]" style={{ color: C.muted }}>
          <thead><tr style={{ color: "#555" }}><th className="text-left font-medium pb-1">Ticker</th><th className="text-left font-medium pb-1">Strategy</th><th className="text-left font-medium pb-1">Score</th><th className="text-left font-medium pb-1">Regime</th></tr></thead>
          <tbody>
            {SCAN_ROWS.map(([t, s, sc, r]) => (
              <tr key={t} className="border-t" style={{ borderColor: C.border }}>
                <td className="py-1.5 font-bold" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{t}</td>
                <td>{s}</td>
                <td style={{ color: C.accent, fontFamily: "'JetBrains Mono', monospace" }}>{sc}</td>
                <td>{r}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[10px] leading-relaxed" style={{ color: C.muted }}>
          Scores are computed from regime + technical + fundamental inputs across curated watchlists.
        </p>
      </Panel>
      <Panel title="Portfolio risk — VaR / CVaR &amp; drawdown floor" C={C} testid="showcase-risk">
        {[["Value at Risk (95%, 1d)", "−$1,240", 31], ["CVaR (95%, 1d)", "−$1,980", 49], ["Drawdown vs. floor", "−3.2% / −10%", 32]].map(([l, v, w]) => (
          <div key={l} className="mb-2">
            <div className="flex justify-between text-[10px] mb-0.5">
              <span style={{ color: C.muted }}>{l}</span>
              <span style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{v}</span>
            </div>
            <div className="h-1.5 rounded" style={{ background: C.border }}>
              <div className="h-1.5 rounded" style={{ width: `${w}%`, background: C.amber }} />
            </div>
          </div>
        ))}
        <p className="mt-2 text-[10px] leading-relaxed" style={{ color: C.muted }}>
          Drawdown and VaR/CVaR are computed from portfolio positions — monitoring, not a recommendation to act.
        </p>
      </Panel>
    </div>
    <p className="mt-4 text-[10px] max-w-2xl" style={{ color: "#555" }} data-testid="showcase-disclaimer">
      The demo above uses precomputed historical data (Jan 2024 – Dec 2025); the sample panels use illustrative data. MarkovLab is analytical software for self-directed investors — for informational and educational purposes only. It does not provide personalized investment advice, does not know your circumstances, and never places or recommends orders.
    </p>
  </div>
);
